import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { headlessGet } from '../../lib/api';

interface CollectionBlockProps {
  typeSlug?: string;
  collectionSlug?: string; // alias used by the CMS block definition field key
  title?: string;
  maxItems?: number;
  imageField?: string;
  excerptField?: string;
  columns?: 2 | 3 | 4;
}

const COLS: Record<number, string> = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};

interface CollectionEntry {
  id: string;
  title: string;
  slug?: string | null;
  dataJson: Record<string, unknown>;
  publishedAt: string | null;
}

const COLLECTION_PREFIX = import.meta.env.VITE_COLLECTION_PREFIX ?? 'c';

export function CollectionBlock({ props }: { props: CollectionBlockProps }) {
  const { typeSlug: typeSlugProp, collectionSlug, title, maxItems = 10, imageField, excerptField, columns = 3 } = props;
  const typeSlug = typeSlugProp ?? collectionSlug;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['collection-block', typeSlug, maxItems],
    queryFn: () =>
      headlessGet<{ entries: CollectionEntry[] }>(
        `/collections/${encodeURIComponent(typeSlug!)}?limit=${maxItems}`,
      ),
    enabled: !!typeSlug,
    staleTime: 60_000,
  });

  const entries = data?.entries ?? [];

  if (!typeSlug) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-8 text-center">
        <p className="text-sm text-zinc-400">No collection configured.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <svg className="w-5 h-5 animate-spin text-zinc-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
        </svg>
      </div>
    );
  }

  if (isError || entries.length === 0) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-8 text-center">
        <p className="text-sm text-zinc-400">
          {isError ? 'Failed to load content.' : 'No entries available.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {title && <h2 className="text-xl font-semibold text-zinc-900">{title}</h2>}
      <div className={`grid gap-4 ${COLS[columns] ?? COLS[3]}`}>
        {entries.map((entry) => {
          const d = entry.dataJson ?? {};
          const imageUrl = imageField
            ? (d[imageField] as string | undefined)
            : (d.imageUrl as string | undefined) ?? (d.image as string | undefined) ?? (d.cover as string | undefined);
          const excerpt = excerptField
            ? (d[excerptField] as string | undefined)
            : (d.excerpt as string | undefined) ?? (d.summary as string | undefined) ?? (d.description as string | undefined);
          const entryPath = `/${COLLECTION_PREFIX}/${encodeURIComponent(typeSlug)}/${encodeURIComponent(entry.slug ?? entry.id)}`;

          return (
            <Link
              key={entry.id}
              to={entryPath}
              className="block bg-white border border-zinc-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              {imageUrl && (
                <div className="aspect-video overflow-hidden bg-zinc-100">
                  <img src={imageUrl} alt={entry.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="p-4 space-y-1.5">
                <h3 className="font-semibold text-zinc-900 text-sm leading-snug line-clamp-2">
                  {entry.title}
                </h3>
                {excerpt && <p className="text-xs text-zinc-500 line-clamp-3">{excerpt}</p>}
                {entry.publishedAt && (
                  <p className="text-[10px] text-zinc-400">
                    {new Date(entry.publishedAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
